import type { HelpCenterSiteTreeNode, SiteNavigationSplit } from './types';

export type SiteNavigationMeasurements = {
	availableWidth: number | null;
	gapWidth: number;
	itemWidths: Readonly<Record<string, number>>;
	moreWidth: number;
};

// Allow 1px of sub-pixel rounding so the split does not flap.
const COMPARISON_TOLERANCE = 1;

const getRowWidth = ({
	itemWidths,
	items,
	gapWidth,
	moreWidth,
	showMore,
}: Omit<SiteNavigationMeasurements, 'availableWidth'> & {
	items: HelpCenterSiteTreeNode[];
	showMore: boolean;
}): number => {
	const visibleItemCount = items.length + (showMore ? 1 : 0);
	const visibleGapCount = Math.max(visibleItemCount - 1, 0);
	// An unmeasured item is unfittable, so it stays in More.
	const promotedItemWidth = items.reduce(
		(total, { id }) => total + (itemWidths[id] ?? Number.MAX_SAFE_INTEGER),
		0,
	);

	return promotedItemWidth + (showMore ? moreWidth : 0) + visibleGapCount * gapWidth;
};

export const splitSiteNavigation = (
	items: readonly HelpCenterSiteTreeNode[],
	measurements: SiteNavigationMeasurements,
): SiteNavigationSplit => {
	const conservativeSplit = { promotedItems: [], overflowItems: [...items] };

	if (measurements.availableWidth === null || items.length === 0) {
		return conservativeSplit;
	}
	const availableWidth = measurements.availableWidth;

	const fits = (candidateItems: HelpCenterSiteTreeNode[], showMore: boolean): boolean =>
		getRowWidth({ ...measurements, items: candidateItems, showMore }) <=
		availableWidth + COMPARISON_TOLERANCE;

	// "More" is optional when every item fits.
	if (fits([...items], false)) {
		return { promotedItems: [...items], overflowItems: [] };
	}

	// Reserve "More" and promote root items left to right while they still fit beside it.
	const promotedItems: HelpCenterSiteTreeNode[] = [];
	for (const item of items) {
		const candidateItems = [...promotedItems, item];
		if (!fits(candidateItems, true)) {
			break;
		}
		promotedItems.push(item);
	}

	return {
		promotedItems,
		overflowItems: items.slice(promotedItems.length),
	};
};
